package com.shadowings.gero.presentation.screen

import com.shadowings.gero.presentation.util.DevicePreviews
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.BasicAlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.hapticfeedback.HapticFeedbackType
import androidx.compose.ui.platform.LocalClipboardManager
import androidx.compose.ui.platform.LocalHapticFeedback
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.semantics.testTag
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import com.shadowings.gero.presentation.component.MdCWord
import com.shadowings.gero.presentation.model.Word
import com.shadowings.gero.presentation.ui.AppTheme
import com.shadowings.gero.presentation.ui.LocalIsDarkMode
import com.shadowings.gero.presentation.util.DeviceClass
import com.shadowings.gero.presentation.util.rememberDeviceInfo
import compose.icons.FeatherIcons
import compose.icons.feathericons.Check
import compose.icons.feathericons.Copy
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import gero.composeapp.generated.resources.*

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun WordDetailDialog(
    word: Word,
    seen: Boolean,
    isFavorite: Boolean,
    languageCode: String,
    onToggleFavorite: () -> Unit,
    onDismiss: () -> Unit
) {
    BasicAlertDialog(
        onDismissRequest = onDismiss
    ) {
        WordDetailDialogContent(
            word = word,
            seen = seen,
            isFavorite = isFavorite,
            languageCode = languageCode,
            onToggleFavorite = onToggleFavorite,
            onDismiss = onDismiss
        )
    }
}

@Composable
fun WordDetailDialogContent(
    word: Word,
    seen: Boolean,
    isFavorite: Boolean,
    languageCode: String,
    onToggleFavorite: () -> Unit,
    onDismiss: () -> Unit,
    modifier: Modifier = Modifier
) {
    val hapticFeedback = LocalHapticFeedback.current
    val localizedMeaning = word.getRawMeaning(languageCode)
    val deviceInfo = rememberDeviceInfo()
    val isWebFull = deviceInfo.deviceClass == DeviceClass.WEB_FULL
    val wordGlyphSize = if (isWebFull) 120.dp else 80.dp
    val variantGlyphSize = if (isWebFull) 84.dp else 56.dp

    Surface(
        modifier = modifier.fillMaxWidth().padding(16.dp),
        shape = RoundedCornerShape(24.dp),
        color = MaterialTheme.colorScheme.surface,
        tonalElevation = 6.dp
    ) {
        Column(
            modifier = Modifier.padding(24.dp).fillMaxWidth(),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            // Titolo (Significato o sconosciuto) con pulsante preferito se visto
            Box(
                modifier = Modifier.fillMaxWidth(),
                contentAlignment = Alignment.Center
            ) {
                Text(
                    text = if (seen) localizedMeaning else stringResource(Res.string.word_unknown_title),
                    style = MaterialTheme.typography.titleLarge.copy(fontWeight = FontWeight.Bold),
                    textAlign = TextAlign.Center,
                    modifier = Modifier.align(Alignment.Center)
                )
                if (seen) {
                    IconButton(
                        onClick = {
                            hapticFeedback.performHapticFeedback(HapticFeedbackType.LongPress)
                            onToggleFavorite()
                        },
                        modifier = Modifier
                            .align(Alignment.CenterEnd)
                            .semantics { testTag = "word_detail_btn_toggle_favorite" }
                    ) {
                        Icon(
                            painter = painterResource(if (isFavorite) Res.drawable.favourite_on else Res.drawable.favourite_off),
                            contentDescription = stringResource(Res.string.toggle_favorite_desc),
                            tint = if (isFavorite && LocalIsDarkMode.current) Color.White else Color.Unspecified,
                            modifier = Modifier.size(24.dp)
                        )
                    }
                }
            }

            // Scrittura geroglifica
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .background(
                        color = if (seen) MaterialTheme.colorScheme.primaryContainer else MaterialTheme.colorScheme.surfaceVariant,
                        shape = RoundedCornerShape(16.dp)
                    )
                    .padding(16.dp)
                    .alpha(if (seen) 1f else 0.25f),
                contentAlignment = Alignment.Center
            ) {
                MdCWord(
                    word = word.gardiner,
                    rtl = false,
                    cart = false,
                    size = wordGlyphSize
                )
            }

            // Dettagli parole
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(6.dp)
            ) {
                if (seen) {
                    DetailRow(label = stringResource(Res.string.label_meaning), value = localizedMeaning)
                    DetailRow(label = stringResource(Res.string.label_reading), value = word.reading)
                    GardinerCodeRow(gardiner = word.gardiner)

                    val localizedNote = word.getNote(languageCode)
                    if (localizedNote.isNotBlank()) {
                        Spacer(modifier = Modifier.height(4.dp))
                        Text(
                            text = localizedNote,
                            style = MaterialTheme.typography.bodyMedium,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                    }

                    val hasAlt1 = !word.alt1.isNullOrBlank()
                    val hasAlt2 = !word.alt2.isNullOrBlank()
                    if (hasAlt1 || hasAlt2) {
                        Spacer(modifier = Modifier.height(8.dp))
                        Text(
                            text = stringResource(Res.string.label_variants),
                            style = MaterialTheme.typography.bodyMedium.copy(fontWeight = FontWeight.Bold),
                            color = MaterialTheme.colorScheme.onSurface
                        )
                        Column(
                            modifier = Modifier.fillMaxWidth().padding(top = 4.dp),
                            verticalArrangement = Arrangement.spacedBy(8.dp),
                            horizontalAlignment = Alignment.CenterHorizontally
                        ) {
                            listOfNotNull(word.alt1, word.alt2).filter { it.isNotBlank() }
                                .forEach { altGardiner ->
                                    Box(
                                        modifier = Modifier
                                            .background(
                                                color = MaterialTheme.colorScheme.surfaceVariant.copy(
                                                    alpha = 0.5f
                                                ),
                                                shape = RoundedCornerShape(12.dp)
                                            )
                                            .border(
                                                width = 1.dp,
                                                color = MaterialTheme.colorScheme.outline.copy(
                                                    alpha = 0.2f
                                                ),
                                                shape = RoundedCornerShape(12.dp)
                                            )
                                            .padding(horizontal = 24.dp, vertical = 8.dp),
                                        contentAlignment = Alignment.Center
                                    ) {
                                        MdCWord(
                                            word = altGardiner,
                                            rtl = false,
                                            cart = false,
                                            size = variantGlyphSize
                                        )
                                    }
                                }
                        }
                    }
                } else {
                    Spacer(modifier = Modifier.height(8.dp))
                    Text(
                        text = stringResource(Res.string.word_not_seen_hint),
                        color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.7f),
                        style = MaterialTheme.typography.bodyMedium,
                        modifier = Modifier.fillMaxWidth(),
                        textAlign = TextAlign.Center
                    )
                }
            }

            Spacer(modifier = Modifier.height(8.dp))

            // Pulsante di conferma
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.End
            ) {
                Button(
                    onClick = {
                        hapticFeedback.performHapticFeedback(HapticFeedbackType.LongPress)
                        onDismiss()
                    },
                    colors = ButtonDefaults.buttonColors(
                        containerColor = MaterialTheme.colorScheme.primary,
                        contentColor = MaterialTheme.colorScheme.onPrimary
                    ),
                    modifier = Modifier.semantics { testTag = "word_detail_btn_close" }
                ) {
                    Text(stringResource(Res.string.chiudi_btn))
                }
            }
        }
    }
}

@DevicePreviews
@Preview
@Preview(name = "Dark", uiMode = 0x20, showBackground = true)
@Composable
private fun WordDetailDialogPreview() {
    AppTheme {
        Surface {
            WordDetailDialogContent(
                word = Word(
                    id = "sole",
                    meaning = "sole",
                    reading = "ra",
                    gardiner = "N5:Z1",
                    note = "Termine per indicare il dio Ra",
                    alt1 = "N5",
                    alt2 = null
                ),
                seen = true,
                isFavorite = false,
                languageCode = "it",
                onToggleFavorite = {},
                onDismiss = {}
            )
        }
    }
}

@Composable
private fun GardinerCodeRow(gardiner: String) {
    val clipboardManager = LocalClipboardManager.current
    val hapticFeedback = LocalHapticFeedback.current
    val coroutineScope = rememberCoroutineScope()
    var copied by remember { mutableStateOf(false) }

    // Title on its own line, then the code centered across the full width with
    // the copy button pinned to the right (the text is padded on both sides by
    // the button's width so it stays truly centered without overlapping it).
    Column(
        modifier = Modifier.fillMaxWidth().padding(vertical = 2.dp),
        verticalArrangement = Arrangement.spacedBy(4.dp)
    ) {
        Text(
            text = stringResource(Res.string.label_gardiner_code),
            style = MaterialTheme.typography.bodyMedium.copy(fontWeight = FontWeight.Bold),
            color = MaterialTheme.colorScheme.onSurfaceVariant
        )
        Box(
            modifier = Modifier.fillMaxWidth(),
            contentAlignment = Alignment.Center
        ) {
            Text(
                text = gardiner,
                style = MaterialTheme.typography.bodyMedium,
                fontFamily = FontFamily.Monospace,
                color = MaterialTheme.colorScheme.onSurface,
                textAlign = TextAlign.Center,
                modifier = Modifier.fillMaxWidth().padding(horizontal = 36.dp)
            )
            IconButton(
                onClick = {
                    hapticFeedback.performHapticFeedback(HapticFeedbackType.LongPress)
                    clipboardManager.setText(AnnotatedString(gardiner))
                    copied = true
                    coroutineScope.launch {
                        delay(1500)
                        copied = false
                    }
                },
                modifier = Modifier
                    .align(Alignment.CenterEnd)
                    .size(32.dp)
                    .semantics { testTag = "word_detail_btn_copy_gardiner" }
            ) {
                Icon(
                    imageVector = if (copied) FeatherIcons.Check else FeatherIcons.Copy,
                    contentDescription = stringResource(Res.string.builder_copy_btn),
                    tint = if (copied) Color(0xFF4CAF50) else MaterialTheme.colorScheme.primary,
                    modifier = Modifier.size(18.dp)
                )
            }
        }
    }
}
